import {
  Alert,
  AlertDescription,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@tinct/web-components';
import { ScimWorkspaceStatus } from '@tinct/core-api-models';
import { InfoIcon } from 'lucide-react';

type StateCopy = {
  label: string;
  variant: 'secondary' | 'success';
  description: string;
};

/** The one word and one sentence a member gets per state; nothing about the tokens or the directory. */
export const SCIM_STATE_COPY: Record<ScimWorkspaceStatus, StateCopy> = {
  disabled: {
    label: 'Off',
    variant: 'secondary',
    description: 'Members are added and removed from this workspace by its admins.',
  },
  enabled: {
    label: 'On',
    variant: 'success',
    description:
      "The workspace's identity provider adds, updates and deactivates its members from the company directory.",
  },
};

export type ScimAdminOnlyCardProps = {
  /** `organization.scimStatus`; `null` while unknown. */
  status: ScimWorkspaceStatus | null;
};

/**
 * What an editor or a viewer sees of SCIM provisioning: whether it is on, and nothing else. The API
 * reserves every SCIM read to the workspace's admins (403); the state comes off the organization,
 * as for single sign-on. No token action, no request of its own.
 */
export function ScimAdminOnlyCard({ status }: ScimAdminOnlyCardProps) {
  const copy = status ? SCIM_STATE_COPY[status] : null;
  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="text-xl">Provisioning (SCIM)</CardTitle>
        <CardDescription>
          This workspace can let its identity provider create, update and
          deactivate its members from a directory.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {copy && (
          <div className="flex items-start gap-3">
            <Badge variant={copy.variant} className="mt-0.5 shrink-0">
              {copy.label}
            </Badge>
            <span className="text-sm text-muted-foreground">{copy.description}</span>
          </div>
        )}
        <Alert>
          <InfoIcon />
          <AlertDescription>
            Only workspace admins can change this setting.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
